import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { UsersService } from 'src/app/users/users.service';
import {
	ChargerManufacturer,
	ChargerManufacturerCorrection,
	ChargerManufacturerSaveResponse,
	ChargerManufacturersService
} from './charger-manufacturers.service';

type Tab = 'manufacturers' | 'corrections';

@Component({
	selector: 'app-charger-manufacturers',
	templateUrl: './charger-manufacturers.component.html',
	styleUrls: ['./charger-manufacturers.component.css']
})
export class ChargerManufacturersComponent implements OnInit, AfterViewInit {
	@ViewChild('manufacturerModal') manufacturerModal: ModalComponent;
	@ViewChild('deleteManufacturerModal') deleteManufacturerModal: ModalComponent;
	@ViewChild('correctionModal') correctionModal: ModalComponent;
	@ViewChild('deleteCorrectionModal') deleteCorrectionModal: ModalComponent;

	activeTab: Tab = 'manufacturers';
	hasWriteAccess = false;

	manufacturers: ChargerManufacturer[] = [];
	corrections: ChargerManufacturerCorrection[] = [];
	manufacturersTableData: TableData[] = [];
	correctionsTableData: TableData[] = [];

	editedManufacturer = { id: 0, name: '' };
	manufacturerToDelete: ChargerManufacturer = null;
	editedCorrection = { id: 0, incorrectValue: '', manufacturerId: null as number };
	correctionToDelete: ChargerManufacturerCorrection = null;
	modalError = '';

	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: true,
		pageSize: 20
	};

	manufacturersColumns: ColumnsConfig[] = [];
	correctionsColumns: ColumnsConfig[] = [];

	constructor(
		private router: Router,
		private translateService: TranslateService,
		private usersService: UsersService,
		private sideMenuService: SideMenuService,
		private notificationMessage: NotificationMessageService,
		public chargerManufacturersService: ChargerManufacturersService
	) { }

	ngOnInit() {
		if (!this.usersService.hasAccessPermission(null, 'noc'))
			return this.router.navigate(['/unauthorized']);

		this.hasWriteAccess = this.usersService.hasAccessPermission(null, 'noc', 'write');
		this.sideMenuService.hide();
		this.setColumns();
		this.loadManufacturers();
		this.loadCorrections();
		return;
	}

	ngAfterViewInit() {
		this.manufacturerModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed)
				this.saveManufacturer();
		});

		this.deleteManufacturerModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed)
				this.deleteManufacturer();
		});

		this.correctionModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed)
				this.saveCorrection();
		});

		this.deleteCorrectionModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed)
				this.deleteCorrection();
		});
	}

	setColumns() {
		const actionColumns: ColumnsConfig[] = this.hasWriteAccess ? [
			{ key: 'edit', name: this.translateService.instant('g.edit'), type: 'icon', hasSort: false, hasFilter: false },
			{ key: 'delete', name: this.translateService.instant('g.delete'), type: 'icon', hasSort: false, hasFilter: false }
		] : [];

		this.manufacturersColumns = [
			this.dataColumn('name', 'manufacturer'),
			{ ...this.dataColumn('rules_count', 'rules_count'), type: 'number', hasFilter: false },
			...actionColumns
		];

		this.correctionsColumns = [
			this.dataColumn('incorrect_value', 'incorrect_value'),
			this.dataColumn('corrected_value', 'corrected_value'),
			...actionColumns
		];
	}

	private dataColumn(key: string, labelKey: string): ColumnsConfig {
		const name = this.translateService.instant('charger_manufacturers.' + labelKey);
		return { key, name, type: 'string', hasSort: true, hasFilter: true };
	}

	switchTab(tab: Tab) {
		this.activeTab = tab;
	}

	loadManufacturers() {
		this.chargerManufacturersService.getManufacturers().subscribe((manufacturers: ChargerManufacturer[]) => {
			this.manufacturers = manufacturers || [];
			this.manufacturersTableData = this.manufacturers.map(manufacturer => ({
				name: { value: manufacturer.name },
				rules_count: { value: manufacturer.rules_count },
				edit: { icon: 'edit', action: () => this.openManufacturerModal(manufacturer) },
				delete: { icon: 'trash', iconColor: '#dc3545', action: () => this.confirmDeleteManufacturer(manufacturer) }
			}));
		});
	}

	loadCorrections() {
		this.chargerManufacturersService.getCorrections().subscribe((corrections: ChargerManufacturerCorrection[]) => {
			this.corrections = corrections || [];
			this.correctionsTableData = this.corrections.map(correction => ({
				incorrect_value: { value: correction.incorrect_value },
				corrected_value: { value: correction.corrected_value },
				edit: { icon: 'edit', action: () => this.openCorrectionModal(correction) },
				delete: { icon: 'trash', iconColor: '#dc3545', action: () => this.confirmDeleteCorrection(correction) }
			}));
		});
	}

	// a rename changes the corrected value shown on the rules too, so both tabs are reloaded after a manufacturer change
	reloadAll() {
		this.loadManufacturers();
		this.loadCorrections();
	}

	openManufacturerModal(manufacturer?: ChargerManufacturer) {
		this.modalError = '';
		this.editedManufacturer = { id: manufacturer?.id || 0, name: manufacturer?.name || '' };
		this.manufacturerModal.show();
	}

	saveManufacturer() {
		const { id, name } = this.editedManufacturer;
		this.chargerManufacturersService.saveManufacturer(id, name).subscribe(response => {
			if (this.showModalError(response))
				return;

			this.manufacturerModal.hide();
			this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
			this.reloadAll();
		});
	}

	confirmDeleteManufacturer(manufacturer: ChargerManufacturer) {
		this.manufacturerToDelete = manufacturer;
		this.deleteManufacturerModal.show();
	}

	deleteManufacturer() {
		if (!this.manufacturerToDelete)
			return;

		this.chargerManufacturersService.deleteManufacturer(this.manufacturerToDelete.id).subscribe(response => {
			this.manufacturerToDelete = null;
			this.showDeleteResult(response);
			this.reloadAll();
		});
	}

	openCorrectionModal(correction?: ChargerManufacturerCorrection) {
		this.modalError = '';
		this.editedCorrection = {
			id: correction?.id || 0,
			incorrectValue: correction?.incorrect_value || '',
			manufacturerId: correction?.manufacturer_id || null
		};
		this.correctionModal.show();
	}

	saveCorrection() {
		const { id, incorrectValue, manufacturerId } = this.editedCorrection;
		if (!manufacturerId) {
			this.modalError = this.translateService.instant('charger_manufacturers.errors.select_corrected_value');
			return;
		}

		this.chargerManufacturersService.saveCorrection(id, incorrectValue, manufacturerId).subscribe(response => {
			if (this.showModalError(response))
				return;

			this.correctionModal.hide();
			this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
			this.reloadAll();
		});
	}

	confirmDeleteCorrection(correction: ChargerManufacturerCorrection) {
		this.correctionToDelete = correction;
		this.deleteCorrectionModal.show();
	}

	deleteCorrection() {
		if (!this.correctionToDelete)
			return;

		this.chargerManufacturersService.deleteCorrection(this.correctionToDelete.id).subscribe(response => {
			this.correctionToDelete = null;
			this.showDeleteResult(response);
			this.reloadAll();
		});
	}

	private showModalError(response: ChargerManufacturerSaveResponse): boolean {
		if (!response?.err)
			return false;

		const max = this.chargerManufacturersService.maxNameLength;
		this.modalError = this.translateService.instant('charger_manufacturers.errors.' + response.err, { max });
		return true;
	}

	// not_found means another user deleted it first, the reload then shows the current list
	private showDeleteResult(response: ChargerManufacturerSaveResponse) {
		if (response?.err)
			return this.notificationMessage.setMessage('translate|charger_manufacturers.errors.' + response.err, {clearOnXTimeNavigate: 1});

		this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
	}
}
